import React from 'react';
import { usePropertyValueQuery } from '@hooks/usePropertyValueQuery';
import { useSelector } from 'react-redux';
import { selectCardByIdAndLoanNumber, selectSelectedLoanNumber } from '@store/selectors/widgets.selectors';
import { CodingConstants } from '@constants';
import { HomeValue, HomeValueProvider } from '@fmc-digital/servicing';
import { logUserEvent } from '@analytics/amplifyLogging';
import { camelCase } from '@utils/addressFormat';
import { PropertyAddress } from '@types';

type HomeValueContainerProps = {
    loanNumber: string;
    handleOfferDeepLink: any;
};

const mapAddress = (propertyAddress: PropertyAddress) => {
    if (propertyAddress) {
        if (propertyAddress?.address2) {
            return `${camelCase(propertyAddress?.address1)}, ${camelCase(propertyAddress?.address2)}, ${camelCase(
                propertyAddress?.city
            )}, ${propertyAddress?.state} ${propertyAddress?.zip}`;
        }
        return `${camelCase(propertyAddress?.address1)}, ${camelCase(propertyAddress?.city)}, ${propertyAddress?.state} ${
            propertyAddress?.zip
        }`;
    }
    return 'your address';
};
type Loan = {
    loanNumber?: string;
    propertyAddress?: string;
    estimates?: Estimate[];
    errorMessage?: string;
};

type Estimate = {
    estimatedDate: string;
    estimatedValue: number;
    highEstimate: number;
    lowEstimate: number;
    priorMonthEstimatedPriceChange: number;
    priorMonthEstimatedPricePercentageChange: number;
    confidence: number;
};

const mapMissingMonths = (ests: Estimate[]): Estimate[] => {
    if (ests.length === 0) return [];
    try {
        ests.sort((a, b) => {
            if (a.estimatedDate > b.estimatedDate) {
                return -1;
            }
            return 1;
        });
        const fiveYearDate = ests[0].estimatedDate ? new Date(ests[0].estimatedDate) : new Date();
        fiveYearDate.setFullYear(fiveYearDate.getFullYear() - 5);
        fiveYearDate.setMonth(fiveYearDate.getMonth() - 1);
        const estimates = ests.filter((estimate: Estimate) => {
            if (!estimate.estimatedDate) return false;
            const currentDate = new Date(estimate.estimatedDate);
            if (currentDate > fiveYearDate) return true;

            return false;
        });
        const mappedEstimates = [];
        mappedEstimates.push(estimates[estimates.length - 1]);
        let i = estimates.length - 2;
        while (i >= 0) {
            const estimate = estimates[i];
            const currentDate = new Date(estimate.estimatedDate);
            const previous: Estimate = mappedEstimates[mappedEstimates.length - 1];
            const previousDate = new Date(previous.estimatedDate);
            const nextDate = new Date(previous.estimatedDate);
            nextDate.setUTCDate(15);
            nextDate.setUTCMonth(previousDate.getUTCMonth() + 1);
            if (nextDate.getUTCFullYear() === currentDate.getUTCFullYear() && nextDate.getUTCMonth() === currentDate.getUTCMonth()) {
                mappedEstimates.push({ ...estimate });
                i--;
            } else if (
                previousDate.getUTCFullYear() === currentDate.getUTCFullYear() &&
                previousDate.getUTCMonth() === currentDate.getUTCMonth()
            ) {
                mappedEstimates.pop();
                const prevEst: Estimate = mappedEstimates[mappedEstimates.length - 1];
                const change = estimate.estimatedValue - prevEst.estimatedValue;
                const changePercentage = (100 * change) / prevEst.estimatedValue;
                mappedEstimates.push({
                    ...estimate,
                    priorMonthEstimatedPriceChange: change,
                    priorMonthEstimatedPricePercentageChange: changePercentage
                });
                i--;
            } else {
                mappedEstimates.push({
                    ...previous,
                    priorMonthEstimatedPriceChange: 0,
                    priorMonthEstimatedPricePercentageChange: 0.0,
                    estimatedDate: nextDate.toISOString()
                });
            }
        }
        return mappedEstimates.reverse();
        // eslint-disable-next-line no-empty
    } catch (error) {}
    return [];
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const HomeValueContainer = ({ loanNumber, handleOfferDeepLink }: HomeValueContainerProps) => {
    const homeValueCard = useSelector(state => selectCardByIdAndLoanNumber(state, CodingConstants.homeValueWidgetId));
    const selectedLoanNumber = useSelector(selectSelectedLoanNumber);
    const propertyValueQuery = usePropertyValueQuery(loanNumber);
    const { data, isLoading, isError } = propertyValueQuery;

    if (isLoading) {
        return null;
    }

    const loans = Array.isArray(data?.loans) ? data.loans : null;
    const loanData = loans?.find((loan: any) => loan.loanNumber === loanNumber);
    const isDataError = Boolean(data?.errorMessage);
    const isLoanDataInvalid = !loanData || Boolean(loanData?.errorMessage);
    const isEstimatesMissing = !loanData?.estimates || !Array.isArray(loanData?.estimates) || loanData?.estimates.includes(undefined);

    const hasError = isError || isDataError || isLoanDataInvalid || isEstimatesMissing;
    if (hasError) {
        return null;
    }

    const propertyAddress = mapAddress(selectedLoanNumber?.propertyAddress);
    const estimates = mapMissingMonths(loanData.estimates);
    if (estimates.length === 0) {
        return null;
    }
    const updatedLoanData = { ...loanData, propertyAddress: propertyAddress, estimates: estimates };

    return (
        <HomeValueProvider
            propertyValue={updatedLoanData}
            card={homeValueCard}
            logUserEvent={logUserEvent}
            handleOfferDeepLink={handleOfferDeepLink}
        >
            <HomeValue />
        </HomeValueProvider>
    );
};
